import React, { Suspense, lazy } from 'react'
import { Router, Switch, Route } from 'react-router'
import { createBrowserHistory } from 'history'
import { AccountContextProvider } from './Context/AccountContext'
import { AccountNotificationProvider } from './Context/AccountNotificationContext'
import { ProfileContextProvider } from './Context/ProfileContext'
import { ToastProvider } from './Context/ToastContext'
import NotFound from './Common/NotFound'
import Layout from './Common/Layout'
import Root from './Navigation'
import AccountDetails from './Account'
import { ErrorContextProvider, ErrorDisplay, ErrorBoundary } from './Context/ErrorContext'
import Loader from 'da-frontend-shared-ui-components/lib/Loader'
import { Alert } from './Common/Alert'
import Breadcrumbs from './Navigation/Breadcrumbs'
import AccountLookup from './Navigation/AccountLookup'
import { setGtm, recordPageView, getStrippedPath, recordVirtualView } from './Common/gtm'
import TagManager from 'react-gtm-module'
import * as redirect from './Redirects'

const ConsumerRequests = lazy(() => import('./ConsumerRequests'))
const Customers = lazy(() => import("./Customers"))
const Dealers = lazy(() => import("./Dealers"))

const id = ":id([0-9a-f-]{36})"

/**
 * Navigation Root
 */
const NavigationRoot = () => {
  return (
    <>
      <Breadcrumbs />
      <AccountLookup />

      <Suspense fallback={<Loader />}>
        <Switch>
          <Route path={`/${id}?/:my(My)?/`} exact children={<Root />} />
          <Route path={`/${id}/My/Account/:section(Detail|Users|Groups)`} children={<AccountDetails />} />
          <Route path={`/${id}/:section(Customers)`} children={<Customers />} />
          <Route path={`/${id}/:section(ConsumerRequests)`} children={<ConsumerRequests />} />
          <Route path={`/${id}/:section(Dealers)`} children={<Dealers />} />
          <Route path={`/driver_download`} component={redirect.toDriverDownload} />
          <Route path={`/privacy_policy`} component={redirect.toPrivacyPolicy} />
          <Route path={`/terms_and_conditions`} component={redirect.toTermsAndConditions} />
          <Route component={NotFound} />

        </Switch>
      </Suspense>
    </>
  )
}

/**
 * Wraps context providers
 */
const ContextProviders = ({ children }: { children: React.ReactNode }) => {
  return (<ErrorBoundary>
    <ErrorContextProvider>
      <ProfileContextProvider>
        <ToastProvider>
          <ErrorDisplay closeable />
          <AccountContextProvider>
            <AccountNotificationProvider>
              <Suspense fallback={<></>}>
                {children}
              </Suspense>
            </AccountNotificationProvider>
          </AccountContextProvider>
        </ToastProvider>
      </ProfileContextProvider>
    </ErrorContextProvider>
  </ErrorBoundary>)
}

/**
 * Create a history object
 */
const browserHistory = createBrowserHistory({
  basename: process.env.PUBLIC_URL
})

/**
 * Application Root - Renders routes for application
 */
class App extends React.Component {
  // used to unregister browserHistory listener
  unregisterHistoryListener = undefined;

  componentDidMount() {
    // console.log("%c[  App > componentDidMount  ]", "color:white; background:black");

    // initialize Google TagManager
    setGtm()

    // block executes whenever URL path changes in browser
    // uses unregisterHistoryListener to unregister listener on class destruction
    this.unregisterHistoryListener = browserHistory.listen(location => {

      // create url path with id's stripped out
      const pathString = getStrippedPath(location.pathname, '/', '/')

      // clear site-related dataLayer variables
      TagManager.dataLayer({
        dataLayer: {
          siteId: undefined,
          applicationType: undefined,
          affectedId: undefined,
        }
      })

      pathString ? recordVirtualView(pathString) : recordPageView()
    })
  }

  // unregister history listener before class destruction to prevent memory leak
  componentWillUnmount() {
    this.unregisterHistoryListener()
  }

  render() {
    return (
      <Router history={browserHistory}>
        <Switch>
          <Route path="/login" />
          <Route path={`/${id}?`}>
            <Suspense fallback={<></>}>
              <Layout aboveHeader={<Alert />}>
                <ContextProviders>
                  <NavigationRoot />
                </ContextProviders>
              </Layout>
            </Suspense>
          </Route>
        </Switch>
      </Router>
    )
  }
}

export default App